import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import config from "../../config";
import RelayControlSetting, {
  DEFAULT_RELAY_SETTINGS,
} from "./RelayControlSetting";
import "./MainRelayControl.css";

const CHANNEL_META = [
  { channel: "ch1", name: "인버터" },
  { channel: "ch2", name: "풍속" },
  { channel: "ch3", name: "강우량" },
  { channel: "ch4", name: "미지정" },
];

function formatGuideValue(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return Number.isInteger(num) ? String(num) : String(num);
}

/** 프로토콜: 1=on, 0=off */
function statusFromRelayValue(value) {
  return Number(value) === 1 ? "on" : "off";
}

function MainRelayControl({ onSettingsClick }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_RELAY_SETTINGS);
  const relayData = useSelector((state) => state.relayData);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const res = await fetch(`${config.backendUrl}/api/relay-control-settings`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          const next = { ...DEFAULT_RELAY_SETTINGS };
          Object.keys(DEFAULT_RELAY_SETTINGS).forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) {
              next[key] = data[key];
            }
          });
          setSettings(next);
        }
      } catch (e) {
        console.warn("relay control settings load failed:", e);
      }
    };

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  // 소켓으로 들어온 기준값이 있으면 가이드에 반영 (첫 번째 장치)
  useEffect(() => {
    if (!relayData?.seq?.length) return;
    const i = 0;
    setSettings((prev) => ({
      ...prev,
      ch1_on: relayData.ch1_on[i] ?? prev.ch1_on,
      ch1_off: relayData.ch1_off[i] ?? prev.ch1_off,
      ch2_on: relayData.ch2_on[i] ?? prev.ch2_on,
      ch2_off: relayData.ch2_off[i] ?? prev.ch2_off,
      ch3_on: relayData.ch3_on[i] ?? prev.ch3_on,
      ch3_off: relayData.ch3_off[i] ?? prev.ch3_off,
      ch4_on: relayData.ch4_on[i] ?? prev.ch4_on,
      ch4_off: relayData.ch4_off[i] ?? prev.ch4_off,
    }));
  }, [relayData]);

  const channels = useMemo(() => {
    const statusArr =
      relayData?.relayStatus?.[0] && Array.isArray(relayData.relayStatus[0])
        ? relayData.relayStatus[0]
        : [0, 0, 0, 0];

    return CHANNEL_META.map((meta, idx) => ({
      ...meta,
      status: statusFromRelayValue(statusArr[idx]),
    }));
  }, [relayData]);

  const guideItems = useMemo(
    () => [
      `인버터 차단 기준 ${formatGuideValue(settings.ch1_on)}kW / 차단 유예 ${formatGuideValue(settings.ch1_off)}kW`,
      `풍속 차단 기준 ${formatGuideValue(settings.ch2_on)}m/s / 차단 유예 ${formatGuideValue(settings.ch2_off)}m/s`,
      `강우량 차단 기준 ${formatGuideValue(settings.ch3_on)}mm / 차단 유예 ${formatGuideValue(settings.ch3_off)}mm`,
      `미지정 차단 기준 ${formatGuideValue(settings.ch4_on)} / 차단 유예 ${formatGuideValue(settings.ch4_off)}`,
    ],
    [settings]
  );

  const handleSettingsClick = () => {
    if (typeof onSettingsClick === "function") {
      onSettingsClick();
      return;
    }
    setSettingsOpen(true);
  };

  return (
    <>
      <section className="main-relay-control">
        <div
          className="gauge-panel-card-title main-relay-control__header"
          style={{ borderBottom: "2px solid #DCE0EB" }}
        >
          <div>◎ 접점 제어 현황</div>
          <div style={{ flexGrow: 1 }} />
          <button
            type="button"
            className="main-relay-control__settings"
            onClick={handleSettingsClick}
          >
            설정하기
          </button>
        </div>

        <div className="main-relay-control__body">
          <div className="main-relay-control__channels">
            {channels.map(({ channel, name, status }) => {
              const normalizedStatus = String(status).toLowerCase();
              const isOn = normalizedStatus === "on";

              return (
                <div className="main-relay-control__channel" key={channel}>
                  <div className="main-relay-control__channel-name">
                    {channel}
                  </div>

                  <div
                    className={`main-relay-control__relay ${
                      isOn
                        ? "main-relay-control__relay--on"
                        : "main-relay-control__relay--off"
                    }`}
                  >
                    <span>{name}</span>
                    <strong>{isOn ? "on" : "off"}</strong>
                  </div>
                </div>
              );
            })}
          </div>

          <ul className="main-relay-control__guide">
            {guideItems.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </div>
      </section>

      <RelayControlSetting
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={(next) => setSettings({ ...DEFAULT_RELAY_SETTINGS, ...next })}
      />
    </>
  );
}

export default MainRelayControl;
